import { useEffect, useMemo, useState } from 'react';
import { jsPDF } from 'jspdf';
import { II } from '../../components/common/Icons';
import HrModal from './HrModal';
import api from '../../services/api';
import { useHRData } from '../../context/HRContext';
import T from '../../assets/styles/theme';import "../../assets/styles/HR/OffboardPg.css";

function OffboardPg() {
  const { offboardList, hrEmployees, refreshHRData } = useHRData();
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedOffboardId, setSelectedOffboardId] = useState('');
  const [showSettle, setShowSettle] = useState(false);
  const [suc, setSuc] = useState('');
  const [selEmp, setSelEmp] = useState(null);
  const [settlement, setSettlement] = useState(null);
  const [settleLoading, setSettleLoading] = useState(false);
  const [editField, setEditField] = useState(null);
  const [editVal, setEditVal] = useState('');
  const [editReason, setEditReason] = useState('');
  const [edits, setEdits] = useState([]);
  const [proceedLoading, setProceedLoading] = useState('');
  const [saveLoading, setSaveLoading] = useState(false);
  const [ensureLoading, setEnsureLoading] = useState(false);
  const [lifecycleRows, setLifecycleRows] = useState([]);
  const [selectedReqId, setSelectedReqId] = useState('');
  const [docUpload, setDocUpload] = useState({ relievingLetter: null, experienceLetter: null, fnfStatement: null });
  const [docLoading, setDocLoading] = useState(false);
  const [settleSendLoading, setSettleSendLoading] = useState(false);
  const [pmOptions, setPmOptions] = useState([]);
  const [initiatePmId, setInitiatePmId] = useState('');
  const [initiateLoading, setInitiateLoading] = useState(false);

  const rowsToShow = useMemo(() => {
    if (!selectedOffboardId) return offboardList || [];
    return (offboardList || []).filter((o) => String(o.id) === String(selectedOffboardId));
  }, [offboardList, selectedOffboardId]);

  const selectedEmpMeta = useMemo(() => {
    if (!selectedUserId) return null;
    const uid = String(selectedUserId);
    const emp = (hrEmployees || []).find((e) => String(e.userId || e.user_id || e.id) === uid);
    if (!emp) return null;
    return {
      name: emp.name || emp.full_name || '-',
      empId: emp.emp_id || emp.id || '-',
      designation: emp.role || emp.designation || '-',
      dept: emp.dept || emp.department || '-'
    };
  }, [hrEmployees, selectedUserId]);

  const selectedLifecycleReq = useMemo(() => {
    if (!selEmp) return null;
    const uid = String(selEmp.userId || selEmp.user_id || selEmp.id || '');
    const rows = Array.isArray(lifecycleRows) ? lifecycleRows : [];
    return rows.find((r) => String(r.user_id || r.userId || '') === uid) || null;
  }, [lifecycleRows, selEmp]);

  const selectedReqRow = useMemo(
    () => Array.isArray(lifecycleRows) ? lifecycleRows.find((r) => String(r.req_id) === String(selectedReqId)) || null : null,
    [lifecycleRows, selectedReqId]
  );

  useEffect(() => {
    api.getLifecycleHrRequests('offboarding').
    then((data) => setLifecycleRows(Array.isArray(data) ? data : [])).
    catch(() => setLifecycleRows([]));
    api.getPMs().then((rows) => setPmOptions(Array.isArray(rows) ? rows : [])).catch(() => setPmOptions([]));
  }, []);

  const fmt = (v) => '\u20B9 ' + Number(String(v).replace(/,/g, '') || 0).toLocaleString('en-IN');
  const parse = (v) => Number(String(v).replace(/,/g, '')) || 0;
  const calcTotal = (s) => parse(s.basicDue) + parse(s.leaveEncash) + parse(s.bonus) + parse(s.gratuity) -
  parse(s.advanceDeduct) - parse(s.assetDeduct) - parse(s.noticePay) - parse(s.otherDeduct);

  const onPickEmployee = async (v) => {
    setSelectedUserId(v || '');
    if (!v) {
      setSelectedOffboardId('');
      return;
    }
    setEnsureLoading(true);
    try {
      const row = await api.ensureOffboardingRecord(v);
      setSelectedOffboardId(String(row?.id || ''));
      await refreshHRData?.();
    } catch (e) {
      alert(e.message || 'Failed to load offboarding record');
    } finally {
      setEnsureLoading(false);
    }
  };

  const initiateOffboarding = async () => {
    if (!selectedUserId) return alert('Select employee');
    setInitiateLoading(true);
    try {
      await api.initiateHrOffboarding({
        userId: Number(selectedUserId),
        pmId: initiatePmId ? Number(initiatePmId) : undefined,
        title: 'Offboarding Initiated',
        comment: 'HR initiated offboarding process. Fill resignation form.'
      });
      setSuc('Offboarding initiated. Employee can now fill offboarding form.');
      setTimeout(() => setSuc(''), 3200);
      const data = await api.getLifecycleHrRequests('offboarding').catch(() => []);
      setLifecycleRows(Array.isArray(data) ? data : []);
    } catch (e) {
      alert(e.message || 'Failed to initiate offboarding');
    } finally {
      setInitiateLoading(false);
    }
  };

  const openSettlement = async (emp) => {
    setSelEmp(emp);
    setShowSettle(true);
    setSettleLoading(true);
    setEdits([]);
    try {
      const rows = await api.getLifecycleHrRequests('offboarding').catch(() => []);
      setLifecycleRows(Array.isArray(rows) ? rows : []);
      const data = await api.getSettlement(emp.id);
      setSettlement({
        ...data,
        resignDate: data?.resignDate || emp?.resignDate || '',
        assetClearance: !!data?.assetClearance,
        checklistNote: data?.checklistNote || ''
      });
    } catch (_) {
      setSettlement({
        basicDue: 0, leaveEncash: 0, bonus: 0, gratuity: 0, advanceDeduct: 0, assetDeduct: 0, noticePay: 0, otherDeduct: 0,
        assets: [], leaveBalance: [], resignDate: '', assetClearance: false, checklistNote: ''
      });
    } finally {
      setSettleLoading(false);
    }
  };

  const handleEdit = (field, label) => {
    setEditField({ field, label });
    setEditVal(String(settlement?.[field] ?? '0'));
    setEditReason('');
  };

  const saveEdit = () => {
    if (!editField || !editReason.trim()) return;
    const { field, label } = editField;
    const oldVal = settlement[field];
    setSettlement((prev) => ({ ...prev, [field]: editVal }));
    setEdits((prev) => [{
      field: label,
      oldVal,
      newVal: editVal,
      reason: editReason,
      date: new Date().toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })
    }, ...prev]);
    setEditField(null);
    setSuc('Settlement detail updated locally. Save settlement to persist.');
    setTimeout(() => setSuc(''), 3200);
  };

  const saveSettlement = async () => {
    if (!selEmp || !settlement) return;
    setSaveLoading(true);
    try {
      await api.saveSettlement(selEmp.id, settlement);
      await api.updateOffboarding(selEmp.id, 'resign_date', settlement.resignDate || null);
      await api.updateOffboarding(selEmp.id, 'it_cleared', settlement.assetClearance ? 1 : 0);
      await refreshHRData?.();
      setSuc('Settlement saved successfully.');
      setTimeout(() => setSuc(''), 3500);
    } catch (e) {
      alert(e.message || 'Failed to save settlement');
    } finally {
      setSaveLoading(false);
    }
  };

  const proceedOffboard = async (emp) => {
    if (!window.confirm('Are you sure you want to proceed? This will block employee login and deactivate credentials.')) return;
    setProceedLoading(emp.id);
    try {
      await api.proceedOffboarding(emp.id);
      setSuc(`Offboarding completed. ${emp.name}'s credentials are blocked.`);
      setTimeout(() => setSuc(''), 4200);
      await refreshHRData?.();
    } catch (e) {
      alert(e.message || 'Failed to proceed offboarding');
    } finally {
      setProceedLoading('');
    }
  };

  const uploadFinalDocs = async () => {
    if (!selectedReqId) return;
    if (!docUpload.relievingLetter && !docUpload.experienceLetter && !docUpload.fnfStatement) {
      alert('Upload at least one document');
      return;
    }
    setDocLoading(true);
    try {
      const fd = new FormData();
      fd.append('reqId', selectedReqId);
      if (docUpload.relievingLetter) fd.append('relievingLetter', docUpload.relievingLetter);
      if (docUpload.experienceLetter) fd.append('experienceLetter', docUpload.experienceLetter);
      if (docUpload.fnfStatement) fd.append('fnfStatement', docUpload.fnfStatement);
      await api.hrUploadOffboardingDocs(fd);
      setSuc('Offboarding final documents uploaded.');
      setTimeout(() => setSuc(''), 3200);
    } catch (e) {
      alert(e.message || 'Failed to upload');
    } finally {
      setDocLoading(false);
    }
  };

  const sendFinalSettlement = async (target = 'pm') => {
    if (!selectedReqId || !settlement) return;
    setSettleSendLoading(true);
    try {
      await api.hrSendOffboardingSettlement({
        reqId: selectedReqId,
        target,
        settlement: {
          basicDue: settlement.basicDue || 0,
          leaveEncash: settlement.leaveEncash || 0,
          bonus: settlement.bonus || 0,
          gratuity: settlement.gratuity || 0,
          advanceDeduct: settlement.advanceDeduct || 0,
          assetDeduct: settlement.assetDeduct || 0,
          noticePay: settlement.noticePay || 0,
          otherDeduct: settlement.otherDeduct || 0,
          checklistNote: settlement.checklistNote || '',
          resignDate: settlement.resignDate || null
        }
      });
      setSuc(target === 'pm' ? 'Final settlement sent to manager for approval.' : 'Final settlement sent to employee.');
      setTimeout(() => setSuc(''), 3500);
      const data = await api.getLifecycleHrRequests('offboarding').catch(() => []);
      setLifecycleRows(Array.isArray(data) ? data : []);
    } catch (e) {
      alert(e.message || 'Failed to send settlement');
    } finally {
      setSettleSendLoading(false);
    }
  };

  const sendSettlementToPmFromModal = async () => {
    if (!selectedLifecycleReq?.req_id || !settlement) return alert('No linked offboarding request found for this employee');
    setSettleSendLoading(true);
    try {
      await api.hrSendOffboardingSettlement({
        reqId: selectedLifecycleReq.req_id,
        target: 'pm',
        settlement: {
          basicDue: settlement.basicDue || 0,
          leaveEncash: settlement.leaveEncash || 0,
          bonus: settlement.bonus || 0,
          gratuity: settlement.gratuity || 0,
          advanceDeduct: settlement.advanceDeduct || 0,
          assetDeduct: settlement.assetDeduct || 0,
          noticePay: settlement.noticePay || 0,
          otherDeduct: settlement.otherDeduct || 0,
          checklistNote: settlement.checklistNote || '',
          resignDate: settlement.resignDate || null
        }
      });
      setSuc('Settlement sent to PM from Active Offboarding.');
      setTimeout(() => setSuc(''), 3000);
      const data = await api.getLifecycleHrRequests('offboarding').catch(() => []);
      setLifecycleRows(Array.isArray(data) ? data : []);
    } catch (e) {
      alert(e.message || 'Failed to send settlement to PM');
    } finally {
      setSettleSendLoading(false);
    }
  };

  const sendSettlementToEmployeeFromModal = async () => {
    if (!selectedLifecycleReq?.req_id || !settlement) return alert('No linked offboarding request found for this employee');
    if (String(selectedLifecycleReq?.status || '').toLowerCase() !== 'settlement_approved_pm') {
      return alert('PM approval required before sending settlement to employee');
    }
    setSettleSendLoading(true);
    try {
      await api.hrSendOffboardingSettlement({
        reqId: selectedLifecycleReq.req_id,
        target: 'employee',
        settlement: {
          basicDue: settlement.basicDue || 0,
          leaveEncash: settlement.leaveEncash || 0,
          bonus: settlement.bonus || 0,
          gratuity: settlement.gratuity || 0,
          advanceDeduct: settlement.advanceDeduct || 0,
          assetDeduct: settlement.assetDeduct || 0,
          noticePay: settlement.noticePay || 0,
          otherDeduct: settlement.otherDeduct || 0,
          checklistNote: settlement.checklistNote || '',
          resignDate: settlement.resignDate || null
        }
      });
      setSuc('Settlement sent to Employee from Active Offboarding.');
      setTimeout(() => setSuc(''), 3000);
      const data = await api.getLifecycleHrRequests('offboarding').catch(() => []);
      setLifecycleRows(Array.isArray(data) ? data : []);
    } catch (e) {
      alert(e.message || 'Failed to send settlement to employee');
    } finally {
      setSettleSendLoading(false);
    }
  };

  const downloadSettlementPdf = () => {
    if (!selEmp || !settlement) return;
    const s = settlement;
    const total = calcTotal(s);
    const doc = new jsPDF();
    let y = 16;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text('OFFBOARDING FINAL SETTLEMENT', 14, y);
    y += 8;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`Employee: ${selEmp.name || '-'}`, 14, y);y += 6;
    doc.text(`Emp ID: ${selEmp.empId || selEmp.emp_id || '-'}`, 14, y);y += 6;
    doc.text(`Department: ${selEmp.dept || '-'}`, 14, y);y += 6;
    doc.text(`Resign Date: ${s.resignDate || '-'}`, 14, y);y += 8;

    const rowsPdf = [
    ['Basic Due', s.basicDue || 0],
    ['Leave Encashment', s.leaveEncash || 0],
    ['Bonus', s.bonus || 0],
    ['Gratuity', s.gratuity || 0],
    ['Advance Deduction', -Number(s.advanceDeduct || 0)],
    ['Asset Deduction', -Number(s.assetDeduct || 0)],
    ['Notice Pay', -Number(s.noticePay || 0)],
    ['Other Deduction', -Number(s.otherDeduct || 0)]];


    rowsPdf.forEach(([label, val]) => {
      doc.text(String(label), 14, y);
      doc.text(`INR ${Number(val).toLocaleString('en-IN')}`, 150, y, { align: 'right' });
      y += 6;
    });

    y += 4;
    doc.setFont('helvetica', 'bold');
    doc.text('Net Settlement', 14, y);
    doc.text(`INR ${Number(total).toLocaleString('en-IN')}`, 150, y, { align: 'right' });
    y += 8;
    doc.setFont('helvetica', 'normal');
    doc.text(`Generated: ${new Date().toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })}`, 14, y);

    doc.save(`settlement-${selEmp.empId || selEmp.emp_id || selEmp.id || 'employee'}.pdf`);
  };

  return (
    <div>
      <div className="sec">Offboarding and Exit</div>
      {suc && <div className="toast">{II('chk', 16, { c: '#2563eb' })} {suc}</div>}

      <div className="cd mb16">
        <div className="cd-h">
          <div className="cd-t">Initiate Employee Offboarding</div>
          <div className="cd-s">Select employee and proceed to unlock employee offboarding form</div>
        </div>
        <div className="cd-b">
          <div className="offboardpg-inline-1">
            <select className="fi" value={selectedUserId} onChange={(e) => onPickEmployee(e.target.value)}>
              <option value="">Select employee</option>
              {(hrEmployees || []).map((e) =>
              <option key={e.userId || e.id} value={e.userId || ''}>{e.name} ({e.emp_id || e.id})</option>
              )}
            </select>
            <select className="fi" value={initiatePmId} onChange={(e) => setInitiatePmId(e.target.value)}>
              <option value="">Select PM (optional auto-map)</option>
              {pmOptions.map((pm) => <option key={pm.id} value={pm.id}>{pm.name} ({pm.emp_id})</option>)}
            </select>
            <button className="btn" onClick={initiateOffboarding} disabled={initiateLoading || !selectedUserId}>{initiateLoading ? 'Proceeding...' : 'Proceed'}</button>
          </div>
          {selectedEmpMeta ?
          <div className="offboardpg-inline-2">
              <div className="offboardpg-inline-3">Selected Employee</div>
              <div>{selectedEmpMeta.name} ({selectedEmpMeta.empId})</div>
              <div>{selectedEmpMeta.designation} | {selectedEmpMeta.dept}</div>
            </div> :
          null}
          {selectedOffboardId ? <div className="offboardpg-inline-4">Offboarding record ready: #{selectedOffboardId}</div> : null}
        </div>
      </div>

      <div className="cd mb16">
        <div className="cd-h offboardpg-inline-5">
          <div className="cd-t">Active Offboarding</div>
          <div className="offboardpg-inline-6">
            <select
              className="fi offboardpg-inline-7"
              value={selectedUserId}
              onChange={(e) => onPickEmployee(e.target.value)}>

              
              <option value="">All employees</option>
              {(hrEmployees || []).map((e) =>
              <option key={e.userId || e.id} value={e.userId || ''}>
                  {e.name || 'Employee'} ({e.emp_id || e.id || 'NA'})
                </option>
              )}
            </select>
            {ensureLoading ? <span style={{ "--offboardpg-1-color": T.textMuted }} className="offboardpg-dyn-1">Loading...</span> : null}
          </div>
        </div>
        <div className="cd-b offboardpg-inline-8">
          <table className="tbl">
            <thead>
              <tr>
                <th>ID</th><th>Name</th><th>Dept</th><th>Resign Date</th><th>Last Day</th><th>Assets</th><th>Status</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rowsToShow.length === 0 ?
              <tr>
                  <td colSpan="8" style={{ "--offboardpg-2-color": T.textMuted }} className="offboardpg-dyn-2">No offboarding records</td>
                </tr> :
              rowsToShow.map((o) =>
              <tr key={o.id}>
                  <td className="offboardpg-inline-9">{o.empId || o.id}</td>
                  <td className="offboardpg-inline-10">{o.name}</td>
                  <td>{o.dept}</td>
                  <td>{o.resignDate || '-'}</td>
                  <td>{o.lastDay || '-'}</td>
                  <td><span className={'chip ' + (o.assetsReturned ? 'chip-ok' : 'chip-wn')}>{o.assetsReturned ? 'Cleared' : 'Pending'}</span></td>
                  <td><span className={'chip ' + (o.status === 'Completed' ? 'chip-ok' : o.status === 'In Progress' ? 'chip-ip' : 'chip-wn')}>{o.status}</span></td>
                  <td>
                    <div className="offboardpg-inline-11">
                      <button className="btn-o btn-s" onClick={() => openSettlement(o)}>{II('dollar', 14)} Settlement</button>
                      {o.status !== 'Completed' &&
                    <button
                      className="btn btn-s btn-dn offboardpg-inline-12"
                      disabled={proceedLoading === o.id}
                      onClick={() => proceedOffboard(o)}>

                      
                          {proceedLoading === o.id ? '...' : 'Block & Exit'}
                        </button>
                    }
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="cd mb16">
        <div className="cd-h">
          <div className="cd-t">Final Offboarding Docs and Settlement Dispatch</div>
          <div className="cd-s">Upload documents and send final settlement to PM and employee</div>
        </div>
        <div className="cd-b">
          <select className="fi" value={selectedReqId} onChange={(e) => setSelectedReqId(e.target.value)}>
            <option value="">Select offboarding request</option>
            {lifecycleRows.map((r) =>
            <option key={r.req_id} value={r.req_id}>
                {r.req_id} - {r.user_name || '-'} ({r.status})
              </option>
            )}
          </select>
          <div className="offboardpg-inline-13">
            <div>
              <div className="offboardpg-inline-14">Leaving Letter</div>
              <input className="fi" type="file" onChange={(e) => setDocUpload((p) => ({ ...p, relievingLetter: e.target.files?.[0] || null }))} />
            </div>
            <div>
              <div className="offboardpg-inline-15">Experience Certificate</div>
              <input className="fi" type="file" onChange={(e) => setDocUpload((p) => ({ ...p, experienceLetter: e.target.files?.[0] || null }))} />
            </div>
            <div>
              <div className="offboardpg-inline-16">F&F Statement</div>
              <input className="fi" type="file" onChange={(e) => setDocUpload((p) => ({ ...p, fnfStatement: e.target.files?.[0] || null }))} />
            </div>
          </div>
          <div className="offboardpg-inline-17">
            <button className="btn" disabled={docLoading || !selectedReqId} onClick={uploadFinalDocs}>{docLoading ? 'Uploading...' : 'Upload Final Documents'}</button>
          </div>
        </div>
      </div>

      <HrModal open={showSettle && !!selEmp} onClose={() => {setShowSettle(false);setSelEmp(null);setSettlement(null);}} title={`Final Settlement - ${selEmp?.name || ''}`}>
        {settleLoading ?
        <div style={{ "--offboardpg-3-color": T.textLight }} className="offboardpg-dyn-3">Loading settlement data...</div> :
        settlement ? (() => {
          const s = settlement;
          const total = calcTotal(s);
          const fields = [
          ['basicDue', 'Basic Salary Due', 'earn'],
          ['leaveEncash', 'Leave Encashment', 'earn'],
          ['bonus', 'Bonus / Incentives', 'earn'],
          ['gratuity', 'Gratuity', 'earn'],
          ['advanceDeduct', 'Salary Advance Recovery', 'ded'],
          ['assetDeduct', 'Asset Non-Return Deduction', 'ded'],
          ['noticePay', 'Notice Period Shortfall', 'ded'],
          ['otherDeduct', 'Other Deductions', 'ded']];

          return (
            <div>
              <div style={{ "--offboardpg-4-background": T.accentBg }} className="offboardpg-dyn-4">
                <div style={{ "--offboardpg-5-color": T.accent }} className="offboardpg-dyn-5">Total Settlement Amount</div>
                <div style={{ "--offboardpg-6-color": total >= 0 ? '#2563eb' : '#ef4444' }} className="offboardpg-dyn-6">
                  {total >= 0 ? '+' : '-'}{'\u20B9'} {Math.abs(total).toLocaleString('en-IN')}
                </div>
                <div style={{ "--offboardpg-7-color": T.textLight }} className="offboardpg-dyn-7">Payable to {total >= 0 ? 'employee' : 'company'}</div>
              </div>

              <div className="fg">
                <label className="fl">Resign Date</label>
                <input className="fi" type="date" value={s.resignDate || ''} onChange={(e) => setSettlement((p) => ({ ...p, resignDate: e.target.value }))} />
              </div>
              <div className="fg offboardpg-inline-18">
                <label className="fl offboardpg-inline-19">
                  <input type="checkbox" checked={!!s.assetClearance} onChange={(e) => setSettlement((p) => ({ ...p, assetClearance: e.target.checked }))} />
                  Asset Clearance
                </label>
              </div>
              <div className="fg">
                <label className="fl">Checklist Note</label>
                <textarea className="fta" value={s.checklistNote || ''} onChange={(e) => setSettlement((p) => ({ ...p, checklistNote: e.target.value }))} placeholder="Optional note..." />
              </div>

              <div style={{ "--offboardpg-8-color": T.success }} className="offboardpg-dyn-8">Earnings</div>
              {fields.filter((f) => f[2] === 'earn').map(([k, label], i) =>
              <div key={i} className="offboardpg-inline-20">
                  <div style={{ "--offboardpg-9-color": T.text }} className="offboardpg-dyn-9">{label}</div>
                  <div className="offboardpg-inline-21">
                    <div style={{ "--offboardpg-10-color": T.success }} className="offboardpg-dyn-10">{fmt(s[k])}</div>
                    <button className="btn-o offboardpg-inline-22" onClick={() => handleEdit(k, label)}>{II('edit', 10)} Edit</button>
                  </div>
                </div>
              )}

              <div style={{ "--offboardpg-11-color": T.danger }} className="offboardpg-dyn-11">Deductions</div>
              {fields.filter((f) => f[2] === 'ded').map(([k, label], i) =>
              <div key={i} className="offboardpg-inline-23">
                  <div style={{ "--offboardpg-12-color": T.text }} className="offboardpg-dyn-12">{label}</div>
                  <div className="offboardpg-inline-24">
                    <div style={{ "--offboardpg-13-color": T.danger }} className="offboardpg-dyn-13">-{fmt(s[k])}</div>
                    <button className="btn-o offboardpg-inline-25" onClick={() => handleEdit(k, label)}>{II('edit', 10)} Edit</button>
                  </div>
                </div>
              )}

              <div className="offboardpg-inline-26">
                <div className="offboardpg-inline-27">Net Settlement</div>
                <div style={{ "--offboardpg-14-color": total >= 0 ? '#2563eb' : '#ef4444' }} className="offboardpg-dyn-14">
                  {total >= 0 ? '+' : '-'}{'\u20B9'} {Math.abs(total).toLocaleString('en-IN')}
                </div>
              </div>

              {edits.length > 0 &&
              <div className="offboardpg-inline-28">
                  <div style={{ "--offboardpg-15-color": T.textMuted }} className="offboardpg-dyn-15">Edit History</div>
                  {edits.map((ed, i) =>
                <div key={i} style={{ "--offboardpg-16-color": T.text }} className="offboardpg-dyn-16">
                      <span className="offboardpg-inline-29">{ed.field}:</span> {ed.oldVal} to {ed.newVal} Â· <span style={{ "--offboardpg-17-color": T.textMuted }} className="offboardpg-dyn-17">{ed.reason}</span> <span style={{ "--offboardpg-18-color": T.borderLight }} className="offboardpg-dyn-18">({ed.date})</span>
                    </div>
                )}
                </div>
              }

              <div className="offboardpg-inline-30">
                <button className="btn-o" onClick={() => setShowSettle(false)}>Close</button>
                <button className="btn-o" onClick={downloadSettlementPdf}>Download PDF</button>
                <button className="btn btn-ok" onClick={sendSettlementToPmFromModal} disabled={settleSendLoading || !selectedLifecycleReq?.req_id}>
                  {settleSendLoading ? 'Sending...' : 'Send To PM'}
                </button>
                <button
                  className="btn btn-ok"
                  onClick={sendSettlementToEmployeeFromModal}
                  disabled={settleSendLoading || !selectedLifecycleReq?.req_id || String(selectedLifecycleReq?.status || '').toLowerCase() !== 'settlement_approved_pm'}
                  title={String(selectedLifecycleReq?.status || '').toLowerCase() === 'settlement_approved_pm' ? '' : 'PM approval required'}>
                  
                  {settleSendLoading ? 'Sending...' : 'Send To Employee'}
                </button>
                <button className="btn" onClick={saveSettlement} disabled={saveLoading}>{saveLoading ? 'Saving...' : 'Save Settlement'}</button>
              </div>
            </div>);

        })() : null}
      </HrModal>

      <HrModal open={editField !== null} onClose={() => setEditField(null)} title={`Edit: ${editField?.label || ''}`}>
        {editField &&
        <div>
            <div className="fg"><label className="fl">New Amount (Rs)</label><input className="fi" value={editVal} onChange={(e) => setEditVal(e.target.value)} placeholder="e.g. 50000" /></div>
            <div className="fg"><label className="fl">Reason for Change *</label><textarea className="fta" value={editReason} onChange={(e) => setEditReason(e.target.value)} placeholder="Provide reason for this change (required)..." /></div>
            <div className="offboardpg-inline-31">
              <button className="btn-o" onClick={() => setEditField(null)}>Cancel</button>
              <button className="btn" onClick={saveEdit} disabled={!editReason.trim()}>Save Change</button>
            </div>
          </div>
        }
      </HrModal>
    </div>);

}

export default OffboardPg;